import type { HTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

// Segment variant styles
export const SEGMENT_VARIANTS: Record<string, string> = {
  "striped-gray": "bg-stripes-gray",
  gray: "bg-gray-500",
  yellow: "bg-yellow-500",
  blue: "bg-blue-500",
};

export interface ProgressSegment {
  key: string;
  count: number;
  percentage: number;
  variant: string;
  labelKey: string;
}

export interface SegmentedProgressBarProps
  extends HTMLAttributes<HTMLDivElement> {
  segments: ProgressSegment[];
  width?: number;
  height?: number;
  displayTooltip?: boolean;
}

export interface NormalizedSegment {
  key: string;
  displayPercentage: number;
  variant: string;
}

export const MIN_THRESHOLD = 5;

/**
 * Normalizes segment percentages to ensure visibility of small but non-zero segments.
 * Segments with >0% but <5% are boosted to 5%, and larger segments are reduced proportionally.
 */
export function normalizeSegments(
  segments: ProgressSegment[],
): NormalizedSegment[] {
  const activeSegments = segments.filter((s) => s.percentage > 0);

  if (activeSegments.length === 0) return [];

  // Identify segments that need boosting (>0 but <5%)
  const needsBoost = activeSegments.filter(
    (s) => s.percentage > 0 && s.percentage < MIN_THRESHOLD,
  );
  const boostCount = needsBoost.length;

  if (boostCount === 0) {
    // No adjustment needed
    return activeSegments.map((s) => ({
      key: s.key,
      displayPercentage: s.percentage,
      variant: s.variant,
    }));
  }

  // Calculate the total boost needed and original small segment total
  const boostTotal = boostCount * MIN_THRESHOLD;
  const originalSmallTotal = needsBoost.reduce(
    (sum, s) => sum + s.percentage,
    0,
  );
  const adjustment = boostTotal - originalSmallTotal;

  // Calculate total of segments that don't need boosting
  const largeSegments = activeSegments.filter(
    (s) => s.percentage >= MIN_THRESHOLD,
  );
  const largeTotal = largeSegments.reduce((sum, s) => sum + s.percentage, 0);

  return activeSegments.map((s) => {
    if (s.percentage > 0 && s.percentage < MIN_THRESHOLD) {
      return {
        key: s.key,
        displayPercentage: MIN_THRESHOLD,
        variant: s.variant,
      };
    }
    // Proportionally reduce large segments
    const reduction =
      largeTotal > 0 ? (s.percentage / largeTotal) * adjustment : 0;
    return {
      key: s.key,
      displayPercentage: Math.max(0, s.percentage - reduction),
      variant: s.variant,
    };
  });
}

export const SegmentedProgressBar = ({
  segments,
  width = 140,
  height = 8,
  className,
  displayTooltip = true,
  ...props
}: SegmentedProgressBarProps) => {
  const { t } = useTranslation();
  const normalizedSegments = normalizeSegments(segments);
  const isFullWidth = className?.includes("w-full");

  return (
    <Tooltip open={displayTooltip ? undefined : false}>
      <TooltipTrigger asChild>
        <div
          className={cn("flex gap-0.5 cursor-pointer", className)}
          style={{
            width: isFullWidth ? undefined : `${width}px`,
            height: `${height}px`,
          }}
          {...props}
        >
          {normalizedSegments.map((segment, index) => {
            const isFirst = index === 0;
            const isLast = index === normalizedSegments.length - 1;
            return (
              <div
                key={segment.key}
                className={cn(
                  SEGMENT_VARIANTS[segment.variant] || "bg-gray-200",
                  isFirst && "rounded-l-sm",
                  isLast && "rounded-r-sm",
                )}
                style={{
                  width: `${segment.displayPercentage}%`,
                  minWidth: segment.displayPercentage > 0 ? "1px" : "0",
                }}
              />
            );
          })}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="min-w-[180px]">
        <div className="flex flex-col gap-1 py-1">
          {segments.map((segment) => (
            <div
              key={segment.key}
              className="flex justify-between items-center gap-4"
            >
              <div className="flex items-center gap-2">
                <div
                  className={cn(
                    "w-2 h-2 rounded-sm",
                    SEGMENT_VARIANTS[segment.variant] || "bg-gray-200",
                  )}
                />
                <span className="text-white text-xs">
                  {t(segment.labelKey)}
                </span>
              </div>
              <span className="text-white text-xs font-semibold">
                {Math.round(segment.percentage * 100) / 100}% ({segment.count})
              </span>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

SegmentedProgressBar.displayName = "SegmentedProgressBar";
